import React, { useEffect, useMemo, useState } from "react";
import { FaArrowAltCircleLeft, FaArrowAltCircleRight } from "react-icons/fa";
import { useAuth } from "../../../utils/useAuth";
import { allAvailability, availability } from "../../../api/admin/room";

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getStartDay(year, month) {
  return new Date(year, month, 1).getDay();
}

function formatDate(date) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Dhaka",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

export default function AvaibilityCalendar({ RoomInfo = {} }) {
  const today = new Date();
  const { user } = useAuth();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());

  const [selectedDate, setSelectedDate] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  const [data, setData] = useState([]);
  const [FormData, setFormData] = useState({
    startDate: "",
    endDate: "",
    isAvailable: true,
    rooms: RoomInfo?.roomsAvailable || 0,
    price: RoomInfo?.actualPrice || 0,
    discount: RoomInfo?.discount || 0,
  });
  useEffect(() => {
    if (!RoomInfo?.roomsAvailable) return;

    const temp = [];

    const currentDate = new Date();
    const endDate = new Date(currentDate.getFullYear() + 1, 11, 31);

    while (currentDate <= endDate) {
      temp.push({
        date: formatDate(new Date(currentDate)),
        value: RoomInfo.roomsAvailable,
        isAvailable: true,
        rooms: RoomInfo.roomsAvailable,
        price: RoomInfo.actualPrice,
        discount: RoomInfo.discount,
      });

      currentDate.setDate(currentDate.getDate() + 1);
    }

    setData(temp);
  }, [RoomInfo]);

  const [inputValue, setInputValue] = useState("");

  const calendarData = useMemo(() => {
    const daysInMonth = getDaysInMonth(currentYear, currentMonth);
    const startDay = getStartDay(currentYear, currentMonth);

    const days = [];

    for (let i = 0; i < startDay; i++) {
      days.push(null);
    }

    for (let d = 1; d <= daysInMonth; d++) {
      days.push(new Date(currentYear, currentMonth, d));
    }

    return days;
  }, [currentMonth, currentYear]);

  function nextMonth() {
    const next = new Date(currentYear, currentMonth + 1);
    setCurrentMonth(next.getMonth());
    setCurrentYear(next.getFullYear());
  }

  function prevMonth() {
    const prev = new Date(currentYear, currentMonth - 1);

    if (
      prev.getFullYear() < today.getFullYear() ||
      (prev.getFullYear() === today.getFullYear() &&
        prev.getMonth() < today.getMonth())
    ) {
      return;
    }

    setCurrentMonth(prev.getMonth());
    setCurrentYear(prev.getFullYear());
  }

  function handleDateClick(date) {
    if (!date) return;

    const dateKey = formatDate(date);

    setSelectedDate(date);

    const existing = data.find((item) => item.date === dateKey);

    setFormData({
      startDate: dateKey,
      endDate: dateKey,
      isAvailable: existing?.isAvailable ?? true,
      rooms: existing?.rooms ?? RoomInfo?.roomsAvailable ?? 0,
      price: existing?.price ?? RoomInfo?.actualPrice ?? 0,
      discount: existing?.discount ?? RoomInfo?.discount ?? 0,
    });

    setOpenModal(true);
  }

  function handleSave() {
    const start = new Date(FormData.startDate);
    const end = new Date(FormData.endDate || FormData.startDate);

    const hotelId = RoomInfo?.hotelId;
    const roomId = RoomInfo?.id;
    const createdBy = user?.id;

    const payload = [];

    const updatedData = data.map((item) => {
      const itemDate = new Date(item.date);

      if (itemDate >= start && itemDate <= end) {
        const updated = {
          ...item,
          isAvailable: FormData.isAvailable,
          rooms: Number(FormData.rooms),
          price: Number(FormData.price),
          discount: Number(FormData.discount),
        };

        payload.push({
          id: item.id || null, // 👈 IMPORTANT CHANGE
          hotelId,
          roomId,
          createdBy,
          date: formatDate(itemDate),
          isAvailable: FormData.isAvailable,
          rooms: Number(FormData.rooms),
          price: Number(FormData.price),
          discount: Number(FormData.discount),
        });

        return updated;
      }

      return item;
    });

    setData(updatedData);
    setOpenModal(false);

    availability(payload);
  }
  useEffect(() => {
    if (RoomInfo) {
      setData((prev) => {
        const filtered = prev.filter((item) => item.date !== RoomInfo.date);

        return [...filtered, {}];
      });
    }
  }, [RoomInfo]);
  useEffect(() => {
    if (RoomInfo?.id) {
      allAvailability(setData, RoomInfo.hotelId, RoomInfo.id);
    }
  }, [RoomInfo?.id, RoomInfo?.hotelId]);
  const monthName = new Date(currentYear, currentMonth).toLocaleString(
    "default",
    { month: "long" },
  );
  return (
    <div className="w-full p-4 bg-white rounded-lg">
      <div className="flex justify-between items-center mb-4">
        <button onClick={prevMonth} className="p-2 bg-gray-200 rounded-full">
          <FaArrowAltCircleLeft className="text-md text-gray-400 hover:text-blue-950 duration-300" />
        </button>

        <h2 className="text-lg font-semibold">
          {monthName} {currentYear} ({RoomInfo?.roomName})
        </h2>

        <button onClick={nextMonth} className="p-2 bg-gray-200 rounded-full">
          <FaArrowAltCircleRight className="text-md text-gray-400 hover:text-blue-950 duration-300" />
        </button>
      </div>

      <div className="grid grid-cols-7 text-center font-semibold text-gray-600 mb-2">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d}>{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-2">
        {calendarData.map((date, idx) => {
          const dateKey = date ? formatDate(date) : null;

          const matched = data.find((item) => item.date === dateKey);

          return (
            <div
              key={idx}
              onClick={() => handleDateClick(date)}
              className={`h-20 rounded p-1 border flex flex-col cursor-pointer transition
                ${date ? "border" : "bg-[#eeeeee] cursor-default"}
              `}
            >
              {date && (
                <>
                  <div className="text-sm font-medium">{date.getDate()}</div>

                  <div className="mt-auto">
                    {matched ? (
                      <span
                        className={`text-[14px] px-2 py-[2px] rounded ${matched.isAvailable ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}
                      >
                        {matched.isAvailable
                          ? `${matched.rooms} available`
                          : "Not Available"}
                      </span>
                    ) : (
                      <span className="text-[12px] text-gray-400">
                        Date passed
                      </span>
                    )}
                  </div>

                  {matched && (
                    <div className="mt-1 flex justify-start items-center gap-2">
                      <p>
                        {Number(matched.price) > 0 && (
                          <p className="text-[11px] text-gray-700">
                            ৳{matched.price}
                          </p>
                        )}
                      </p>
                      {Number(matched.discount) > 0 && (
                        <p className="text-[11px] text-orange-600">
                          {matched.discount}% off
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      {openModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center">
          <div className="bg-white p-5 rounded-lg w-96">
            <h2 className="text-lg font-bold mb-4">Room Availability</h2>

            <div className="space-y-3">
              <div>
                <label className="block text-sm mb-1">Start Date</label>

                <input
                  type="date"
                  value={FormData.startDate}
                  onChange={(e) =>
                    setFormData({
                      ...FormData,
                      startDate: e.target.value,
                    })
                  }
                  className="w-full border p-2 rounded focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm mb-1">End Date</label>

                <input
                  type="date"
                  value={FormData.endDate}
                  onChange={(e) =>
                    setFormData({
                      ...FormData,
                      endDate: e.target.value,
                    })
                  }
                  className="w-full border p-2 rounded focus:outline-none"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={FormData.isAvailable}
                  onChange={(e) =>
                    setFormData({
                      ...FormData,
                      isAvailable: e.target.checked,
                      rooms: e.target.checked ? FormData.rooms : 0,
                    })
                  }
                />

                <label>Available</label>
              </div>

              {FormData.isAvailable && (
                <div>
                  <label className="block text-sm mb-1">Number of Rooms</label>

                  <input
                    type="number"
                    min="0"
                    value={FormData.rooms}
                    onChange={(e) =>
                      setFormData({
                        ...FormData,
                        rooms: e.target.value,
                      })
                    }
                    className="w-full border p-2 rounded focus:outline-none"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm mb-1">Price</label>

                <input
                  type="number"
                  min="0"
                  value={FormData.price}
                  onChange={(e) =>
                    setFormData({
                      ...FormData,
                      price: e.target.value,
                    })
                  }
                  className="w-full border p-2 rounded focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm mb-1">Discount (%)</label>

                <input
                  type="number"
                  min="0"
                  value={FormData.discount}
                  onChange={(e) =>
                    setFormData({
                      ...FormData,
                      discount: e.target.value,
                    })
                  }
                  className="w-full border p-2 rounded focus:outline-none"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setOpenModal(false)}
                className="px-3 py-1 bg-gray-200 rounded"
              >
                Cancel
              </button>

              <button
                onClick={handleSave}
                className="px-3 py-1 bg-blue-500 text-white rounded"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
